import {split} from 'utils/split'
import {splitHover} from 'utils/splitHover'
import {createFocusTrap, getFocusable} from 'utils/focusTrap'

import Site from 'stores/Site'
import Component from 'classes/Component'
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";

export default class Header extends Component {
    constructor() {
        super()

        this.el = document.querySelector('.header')
        this.items = []
        this.currentItem = null
        this.scrolled = false
        this.lightIds = [
            'home',
            'stay',
            'spaces',
            'dine',
            'about',
            'history',
            'employer_of_choice',
            'sustainability',
            'ethical_responsibility',
            'specials',
            'contact'
        ]
    }

    init() {
        super.init()

        this.el.querySelectorAll('.menu-item').forEach(el => {
            const item = {
                el,
                link: el.querySelector(':scope > a').getAttribute('href')
            }

            splitHover(el.querySelector('a'))

            if (el.classList.contains('current-menu-item')) {
                this.currentItem = item
            }

            this.items.push(item)
        })

        const ctaLink = this.el.querySelector('.cta-link')
        if (ctaLink) {
            splitHover(ctaLink)
        }

        this.btn = this.el.querySelector('.book-btn')

        this.btn.addEventListener('click', () => {
            Site.drawer.show('stay')
        })

        this.initNav()
    }

    scroll() {
        if (Site.scroll.scroll > 80) {
            if (!this.scrolled) Site.html.classList.add('scrolled')

            Site.emitter.emit('scrolled')

            this.scrolled = true
        } else {
            if (this.scrolled)  Site.html.classList.remove('scrolled')

            Site.emitter.emit('unscrolled')

            this.scrolled = false
        }
    }

    updateHeader(data) {
        if (this.lightIds.includes(data.next.namespace)) {
            this.el.classList.add('header-light')
        } else {
            this.el.classList.remove('header-light')
        }

        this.currentItem && this.currentItem.el.classList.remove('current-menu-item')

        this.items.forEach(item => {
            if (item.link === data.next.url.href) {
                item.el.classList.add('current-menu-item')

                this.currentItem = item
            }
        })
    }

    initNav() {
        Site.navActive = false

        this.navWrapper = document.querySelector('.nav')
        this.navToggle = this.el.querySelector('.nav-toggle')
        this.navClose = this.navWrapper.querySelector('.n-close')
        this.parentItems = []
        this.focusTrap = createFocusTrap(this.navWrapper)

        this.navToggle.addEventListener('click', () => {
            if (Site.navActive) {
                this.hide()
            } else {
                this.show()
            }
        })

        this.navClose.addEventListener('click', () => {
            this.hide()
        })

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && Site.navActive) {
                this.hide()
                this.navToggle.focus()
            }
        })

        if (!Site.isMobile) return

        this.navWrapper.querySelectorAll('.menu-item-has-children').forEach(el => {
            const link = el.querySelector('.menu-item-link')
            const hasLink = !['', '#'].includes(link.getAttribute('href'))
            const headerSelector = hasLink ? '.menu-toggle' : '.menu-item-inner'
            const header = el.querySelector(headerSelector)
            const body = el.querySelector('.sub-menu')

            this.parentItems.push({
                el,
                header,
                body
            })

            gsap.set(body, {
                height: 0,
                overflow: 'hidden'
            })

            header.setAttribute('tabindex', '0')
            header.setAttribute('role', 'button')
            header.setAttribute('aria-expanded', el.classList.contains('active') ? 'true' : 'false')

            const handler = () => {
                if (el.classList.contains('active')) {
                    gsap.to(body, {
                        height: 0,
                        clipPath: 'inset(0% 0% 100% 0%)',
                        duration: .8,
                        immediateRender: false,
                        ease: 'power3.out',
                    })

                    el.classList.remove('active')
                    header.setAttribute('aria-expanded', 'false')
                } else {
                    gsap.to(body, {
                        height: 'auto',
                        clipPath: 'inset(0% 0% 0% 0%)',
                        duration: .8,
                        immediateRender: false,
                        ease: 'power3.out',
                    })

                    el.classList.add('active')
                    header.setAttribute('aria-expanded', 'true')
                }
            }

            this.addEventHandler({
                el: header,
                event: 'click',
                handler
            })

            this.addEventHandler({
                el: header,
                event: 'keydown',
                handler: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handler()
                    }
                }
            })

            if (!hasLink) {
                this.addEventHandler({
                    el: link,
                    event: 'click',
                    handler: e => {
                        e.preventDefault()
                    }
                })
            }
        })
    }

    show() {
        Site.html.classList.add('nav-active')
        Site.scroll.stop()
        Site.navActive = true

        if (this.navToggle) {
            this.navToggle.setAttribute('aria-expanded', 'true')
            this.navToggle.setAttribute('aria-label', 'Close navigation menu')
        }
        if (this.navWrapper) {
            this.navWrapper.removeAttribute('aria-hidden')
            this.navWrapper.removeAttribute('inert')

            this.focusTrap.activate()

            // Move focus into the overlay so the trap has somewhere to hold it.
            const focusable = getFocusable(this.navWrapper)
            if (focusable.length) focusable[0].focus()
        }
    }

    hide() {
        Site.html.classList.remove('nav-active')
        Site.scroll.start()
        Site.navActive = false

        if (this.navToggle) {
            this.navToggle.setAttribute('aria-expanded', 'false')
            this.navToggle.setAttribute('aria-label', 'Open navigation menu')
        }
        if (this.navWrapper) {
            this.navWrapper.setAttribute('aria-hidden', 'true')
            this.navWrapper.setAttribute('inert', '')

            this.focusTrap.deactivate()
        }

        this.parentItems.forEach(({el, header, body}) => {
            gsap.to(body, {
                height: 0,
                clipPath: 'inset(0% 0% 100% 0%)',
                duration: .8,
                immediateRender: false,
                ease: 'power3.out',
            })

            el.classList.remove('active')
            header.setAttribute('aria-expanded', 'false')
        })
    }

    pageLoaded() {
        super.pageLoaded()
    }

    update() {
        super.update()
    }

    resize() {
        super.resize()
    }

    destroy() {
        super.destroy()
    }
}